import { formatMoneyIn, toLocale } from '@zuruflex/i18n';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/cn.js';

export interface MoneyConversion {
  /** Converted amount, in the display currency's minor units. */
  amount: number;
  currency: string;
  /** True when the rate is old enough to be worth flagging. */
  isStale?: boolean;
}

export interface MoneyProps {
  /** Amount in the *source* currency's minor units. */
  amount: number;
  /** The currency actually charged. Always shown. */
  currency: string;
  /**
   * An indicative conversion to show underneath. Omit to show only the
   * charged amount — which is what happens when no rate is known.
   */
  converted?: MoneyConversion | null;
  className?: string;
  /** Styling hook for the primary figure. */
  amountClassName?: string;
  /**
   * Rendered immediately after the charged amount — "per day" and the like.
   * It belongs above the conversion, not below it, or the unit reads as
   * though it qualifies the converted figure.
   */
  suffix?: ReactNode;
}

/**
 * Money, with an optional indicative conversion beneath it.
 *
 * The charged currency is always the prominent one. A converted figure is
 * deliberately smaller, prefixed with `≈` and labelled indicative: a
 * customer must never mistake it for the amount that will leave their
 * account, because bookings settle in the branch's own currency.
 */
export function Money({
  amount,
  currency,
  converted,
  className,
  amountClassName,
  suffix,
}: MoneyProps) {
  const { t, i18n } = useTranslation('common');
  const locale = toLocale(i18n.language);

  const showConversion = converted && converted.currency !== currency;

  return (
    <span className={cn('inline-flex flex-col', className)}>
      <span className={amountClassName}>{formatMoneyIn(amount, currency, locale)}</span>
      {suffix}

      {showConversion ? (
        <span
          className="text-xs text-text-muted"
          title={converted.isStale ? t('money.staleRate') : t('money.indicative')}
        >
          ≈ {formatMoneyIn(converted.amount, converted.currency, locale)}
          {converted.isStale ? ' *' : ''}
        </span>
      ) : null}
    </span>
  );
}
